import React, { useState, useEffect } from 'react';
import './UserManagement.css';
import ManageRoles from './ManageRoles';
import LoadingState from './LoadingState';
import ConfirmModal from './ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const AVAILABLE_ROLES = [
  'Fit Specialist',
  'Assistant Sales Manager',
  'Sales Manager',
  'Business Development Center',
  'Finance & Insurance Manager',
  'Business Manager',
  'General Manager',
  'Service Manager',
  'Parts Manager',
  'Apparel & Licensing Manager',
  'Dashboard'
];

export default function UserManagement({ user, managedOwner, dealershipBetaFeatures = {}, onImpersonate }) {
  // Owners (and platform staff acting as an owner) get the full surface: configuring
  // roles, editing user details, deleting. A General Manager granted 'manage-users'
  // can only add and deactivate/reactivate users in their dealership.
  const isOwner = user?.role === 'OWNER' || user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER' || !!managedOwner;

  // Super admin only: impersonate ("View as") a user. Captured here because the row
  // map below shadows the `user` prop with the per-row user.
  const isSuperAdmin = user?.role === 'ADMIN';
  // Platform staff (admin + site manager): may seed pre-activated users (skip invite).
  const isPlatformStaff = user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER';
  const adminUserId = user?.id;
  const handleViewAs = async (userId) => {
    if (!userId) return;
    try {
      await onImpersonate?.(userId);
    } catch (err) {
      window.alert(err?.message || 'Failed to view as this user');
    }
  };
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealerships, setSelectedDealerships] = useState([]); // Array of selected IDs
  const [selectedRoles, setSelectedRoles] = useState([]); // Array of selected roles
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalError, setAddModalError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManageRolesModal, setShowManageRolesModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filterActive, setFilterActive] = useState('active'); // 'all', 'active', 'inactive'
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUsers, setExpandedUsers] = useState(new Set()); // Track expanded cards on mobile
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rolesSearchInput, setRolesSearchInput] = useState('');
  const [rolesDropdownOpen, setRolesDropdownOpen] = useState(false);
  const [editingRolesSearchInput, setEditingRolesSearchInput] = useState('');
  const [editingRolesDropdownOpen, setEditingRolesDropdownOpen] = useState(false);
  const [dealershipsSearchInput, setDealershipsSearchInput] = useState('');
  const [dealershipsDropdownOpen, setDealershipsDropdownOpen] = useState(false);
  const [editingDealershipsSearchInput, setEditingDealershipsSearchInput] = useState('');
  const [editingDealershipsDropdownOpen, setEditingDealershipsDropdownOpen] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmStyle: 'danger'
  });

  // Alert modal state (for errors/success messages)
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  const [newUser, setNewUser] = useState({
    email: '',
    emailConfirm: '',
    username: '',
    name: '',
    phone: '',
    roles: [], // Multi-select job roles
    dealershipIds: [], // Multi-select dealerships
    activateImmediately: false, // skip email invite, set a temp password
    tempPassword: '' // optional; auto-generated if blank
  });

  useEffect(() => {
    fetchDealerships();
  }, [managedOwner]);

  useEffect(() => {
    const fetchCustomRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/dealerships/role-permissions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include'
        });
        if (!res.ok) return;
        const data = await res.json();
        const permissions = data.rolePermissions || {};
        setCustomRoles(Object.keys(permissions).filter(r => !AVAILABLE_ROLES.includes(r)));
      } catch {}
    };
    fetchCustomRoles();
    window.addEventListener('rolePermissionsUpdated', fetchCustomRoles);
    return () => window.removeEventListener('rolePermissionsUpdated', fetchCustomRoles);
  }, []);

  const toggleDealership = (dealershipId) => {
    setSelectedDealerships(prev => 
      prev.includes(dealershipId)
        ? prev.filter(id => id !== dealershipId)
        : [...prev, dealershipId]
    );
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const toggleUserRole = (role) => {
    setNewUser(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const toggleEditingUserRole = (role) => {
    setEditingUser(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const allRoles = [...AVAILABLE_ROLES, ...customRoles];

  const getFilteredRoles = (searchInput) => {
    if (!searchInput.trim()) {
      return allRoles;
    }
    return allRoles.filter(role =>
      role.toLowerCase().includes(searchInput.toLowerCase())
    );
  };

  const getFilteredDealerships = (searchInput) => {
    if (!searchInput.trim()) {
      return dealerships;
    }
    return dealerships.filter(d =>
      d.name.toLowerCase().includes(searchInput.toLowerCase()) ||
      d.location.toLowerCase().includes(searchInput.toLowerCase())
    );
  };

  const handleAddDealershipFromDropdown = (dealershipId) => {
    if (!newUser.dealershipIds.includes(dealershipId)) {
      setNewUser(prev => ({
        ...prev,
        dealershipIds: [...prev.dealershipIds, dealershipId]
      }));
    }
    setDealershipsSearchInput('');
  };

  const removeDealershipFromNew = (dealershipId) => {
    setNewUser(prev => ({
      ...prev,
      dealershipIds: prev.dealershipIds.filter(id => id !== dealershipId)
    }));
  };

  const handleAddRoleFromDropdown = (role) => {
    if (!newUser.roles.includes(role)) {
      setNewUser(prev => ({
        ...prev,
        roles: [...prev.roles, role]
      }));
    }
    setRolesSearchInput('');
  };

  const handleEditRoleFromDropdown = (role) => {
    const rolesArray = Array.isArray(editingUser.roles) ? editingUser.roles : [];
    if (!rolesArray.includes(role)) {
      setEditingUser(prev => ({
        ...prev,
        roles: [...rolesArray, role]
      }));
    }
    setEditingRolesSearchInput('');
  };

  const removeRoleFromNew = (role) => {
    setNewUser(prev => ({
      ...prev,
      roles: prev.roles.filter(r => r !== role)
    }));
  };

  const removeRoleFromEditing = (role) => {
    setEditingUser(prev => ({
      ...prev,
      roles: Array.isArray(prev.roles) ? prev.roles.filter(r => r !== role) : []
    }));
  };

  const handleAddEditingDealershipFromDropdown = (dealershipId) => {
    const dealershipIdsArray = Array.isArray(editingUser.dealershipIds) ? editingUser.dealershipIds : [];
    if (!dealershipIdsArray.includes(dealershipId)) {
      setEditingUser(prev => ({
        ...prev,
        dealershipIds: [...dealershipIdsArray, dealershipId]
      }));
    }
    setEditingDealershipsSearchInput('');
  };

  const removeDealershipFromEditing = (dealershipId) => {
    setEditingUser(prev => ({
      ...prev,
      dealershipIds: Array.isArray(prev.dealershipIds) ? prev.dealershipIds.filter(id => id !== dealershipId) : []
    }));
  };

  const clearAllFilters = () => {
    setSelectedDealerships([]);
    setSelectedRoles([]);
    setFilterActive('all');
  };

  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(prev => prev.filter(id => id !== value));
    } else if (type === 'role') {
      setSelectedRoles(prev => prev.filter(r => r !== value));
    } else if (type === 'status') {
      setFilterActive('all');
    }
  };

  const fetchDealerships = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      let url = `${API_URL}/api/user-management/dealerships`;
      if (managedOwner) {
        url += `?ownerId=${managedOwner.id}`;
      }

      const response = await fetch(url, {
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch dealerships');
      }

      const data = await response.json();
      setDealerships(data.dealerships);
      // By default, show all dealerships (no filter)
      setSelectedDealerships([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAllDealerships = () => {
    if (selectedDealerships.length === 0) {
      // If none selected, select all
      setSelectedDealerships(dealerships.map(d => d.id));
    } else {
      // If any selected, clear all
      setSelectedDealerships([]);
    }
  };

  // Get all users, optionally filtered by selected dealerships
  const userMap = new Map();
  
  // Collect all unique users from all dealerships
  dealerships.forEach(d => {
    d.users.forEach(u => {
      if (!userMap.has(u.userId)) {
        userMap.set(u.userId, u);
      }
    });
  });
  
  // Filter by selected dealerships if any are selected
  const allUsers = Array.from(userMap.values())
    .filter(u => {
      // Filter by dealership: check if user's dealershipIds includes any selected dealership
      const dealershipMatch = selectedDealerships.length === 0 || 
        (u.dealershipIds && u.dealershipIds.some(id => selectedDealerships.includes(id)));
      
      // Filter by role
      const roleMatch = selectedRoles.length === 0 || selectedRoles.includes(u.role);
      
      return dealershipMatch && roleMatch;
    });

  // Calculate active filters for chips
  const activeFilters = [];
  selectedDealerships.forEach(id => {
    const dealership = dealerships.find(d => d.id === id);
    if (dealership) {
      activeFilters.push({ type: 'dealership', value: id, label: dealership.name });
    }
  });
  selectedRoles.forEach(role => {
    const roleLabel = role === 'MANAGER' ? 'Manager' : role === 'EMPLOYEE' ? 'Employee' : role;
    activeFilters.push({ type: 'role', value: role, label: roleLabel });
  });
  if (filterActive !== 'all') {
    activeFilters.push({ 
      type: 'status', 
      value: filterActive, 
      label: filterActive === 'active' ? 'Active' : 'Inactive' 
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddModalError(null);

    if (!newUser.dealershipIds || newUser.dealershipIds.length === 0) {
      setAddModalError('Please select at least one dealership.');
      return;
    }

    if (newUser.email !== newUser.emailConfirm) {
      setAddModalError('Email addresses do not match.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use the first dealership for the primary endpoint
      const primaryDealershipId = newUser.dealershipIds[0];
      const response = await fetch(
        `${API_URL}/api/user-management/dealerships/${primaryDealershipId}/users`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(newUser)
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAddModalError(data.error || 'Failed to add user.');
        return;
      }

      const createdEmail = newUser.email;
      const createdTempPassword = data.tempPassword;

      // Refresh dealerships
      await fetchDealerships();
      setShowAddModal(false);
      setAddModalError(null);
      setNewUser({
        email: '',
        emailConfirm: '',
        name: '',
        phone: '',
        roles: [],
        dealershipIds: [],
        activateImmediately: false,
        tempPassword: ''
      });

      // If the account was activated immediately, surface the credentials so the
      // admin can log in / hand them off — there is no email invite in this case.
      if (createdTempPassword) {
        setAlertModal({
          isOpen: true,
          title: 'User Created & Activated',
          message: `The account is active now—no email invite was sent.\n\nEmail: ${createdEmail}\nTemporary password: ${createdTempPassword}`
        });
      }
      // Clear search and filters to show updated data
      setSearchTerm('');
      setSelectedDealerships([]);
      setSelectedRoles([]);
      setFilterActive('all');
    } catch (err) {
      setAddModalError(err.message || 'Failed to add user.');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Use the first dealership from the array, or fall back to dealershipId
      const dealershipIdsArray = editingUser.dealershipIds && editingUser.dealershipIds.length > 0 
        ? editingUser.dealershipIds 
        : [editingUser.dealershipId];
      const urlDealershipId = dealershipIdsArray[0];
      
      const response = await fetch(
        `${API_URL}/api/user-management/dealerships/${urlDealershipId}/users/${editingUser.userId}`,
        {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            name: editingUser.name,
            phone: editingUser.phone,
            email: editingUser.email,
            roles: editingUser.roles || [],
            dealershipIds: dealershipIdsArray
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update user');
      }

      // Refresh dealerships
      await fetchDealerships();
      setShowEditModal(false);
      setEditingUser(null);
      // Clear search and filters to show updated data
      setSearchTerm('');
      setSelectedDealerships([]);
      setSelectedRoles([]);
      setFilterActive('all');
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: err.message
      });
    }
  };

  const handleDeactivateUser = async (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Deactivate User',
      message: `Are you sure you want to deactivate ${user.name}?`,
      confirmStyle: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = {};
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          // Use first dealership ID from array, or fall back to dealershipId property
          const dealershipId = user.dealershipIds && user.dealershipIds.length > 0
            ? user.dealershipIds[0]
            : user.dealershipId;

          const response = await fetch(
            `${API_URL}/api/user-management/dealerships/${dealershipId}/users/${user.employmentId}/deactivate`,
            {
              method: 'POST',
              headers,
              credentials: 'include'
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to deactivate user');
          }

          await fetchDealerships();
        } catch (err) {
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: err.message
          });
        }
      }
    });
  };

  const handleReactivateUser = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use first dealership ID from array, or fall back to dealershipId property
      const dealershipId = user.dealershipIds && user.dealershipIds.length > 0
        ? user.dealershipIds[0]
        : user.dealershipId;

      const response = await fetch(
        `${API_URL}/api/user-management/dealerships/${dealershipId}/users/${user.employmentId}/reactivate`,
        {
          method: 'POST',
          headers,
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Failed to reactivate user');

      await fetchDealerships();
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: err.message
      });
    }
  };

  const handleDeleteUser = async (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Permanently delete ${user.name}? This cannot be undone. Their email address will be freed for reuse. Historical reporting data will be preserved.`,
      confirmStyle: 'danger',
      confirmText: 'Delete User',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const dealershipId = (user.dealershipIds && user.dealershipIds.length > 0)
            ? user.dealershipIds[0]
            : (user.anyDealershipId || user.dealershipId);

          const response = await fetch(
            `${API_URL}/api/user-management/dealerships/${dealershipId}/users/${user.userId}/delete`,
            { method: 'DELETE', headers, credentials: 'include' }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete user');
          }

          await fetchDealerships();
        } catch (err) {
          setAlertModal({ isOpen: true, title: 'Error', message: err.message });
        }
      }
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredUsers = allUsers.filter(user => {
    // Filter by active status
    if (filterActive === 'active' && !user.isActive) return false;
    if (filterActive === 'inactive' && user.isActive) return false;

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        user.name.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search) ||
        user.username.toLowerCase().includes(search) ||
        (user.employeeRole && user.employeeRole.toLowerCase().includes(search)) ||
        user.dealershipName.toLowerCase().includes(search)
      );
    }

    return true;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Handle dates
    if (sortField === 'hireDate' || sortField === 'lastLogin') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    // Handle strings
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return <LoadingState message="Loading users and permissions..." fullscreen />;
  }

  if (error) {
    return <div className="user-management-error">Error: {error}</div>;
  }

  if (dealerships.length === 0) {
    return (
      <div className="user-management-empty">
        <p>No dealerships found. Contact an administrator to add dealerships.</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-title">
          <h1>User Management</h1>
          <p>Manage users across your dealerships</p>
        </div>
        {isOwner && (
          <button
            className="btn-manage-roles"
            onClick={() => setShowManageRolesModal(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px', display: 'inline-block'}}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Manage Roles
          </button>
        )}
      </div>

      {/* Filter Bar with Active Filters */}
      <div className="filter-bar">
        <button 
          className={`filter-button ${activeFilters.length > 0 ? 'has-filters' : ''}`}
          onClick={() => setShowFilterPanel(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
          </svg>
          Filters
          {activeFilters.length > 0 && ` (${activeFilters.length})`}
        </button>

        {activeFilters.length > 0 && (
          <div className="active-filters">
            {activeFilters.map((filter, index) => (
              <div key={index} className="active-filter-chip">
                {filter.label}
                <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-body">
              {/* Dealership Filters */}
              <div className="filter-section">
                <div className="filter-section-title">Dealerships</div>
                <div className="filter-options">
                  {dealerships.map(d => {
                    const userCount = d.users.filter(u => 
                      selectedRoles.length === 0 || selectedRoles.includes(u.role)
                    ).length;
                    return (
                      <label 
                        key={d.id} 
                        className={`filter-option ${selectedDealerships.includes(d.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDealerships.includes(d.id)}
                          onChange={() => toggleDealership(d.id)}
                        />
                        <span className="filter-option-label">{d.name}</span>
                        <span className="filter-option-count">{userCount}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Role Filters */}
              <div className="filter-section">
                <div className="filter-section-title">Roles</div>
                <div className="filter-options">
                  <label className={`filter-option ${selectedRoles.includes('MANAGER') ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes('MANAGER')}
                      onChange={() => toggleRole('MANAGER')}
                    />
                    <span className="filter-option-label">Manager</span>
                    <span className="filter-option-count">
                      {allUsers.filter(u => u.role === 'MANAGER').length}
                    </span>
                  </label>
                  <label className={`filter-option ${selectedRoles.includes('EMPLOYEE') ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes('EMPLOYEE')}
                      onChange={() => toggleRole('EMPLOYEE')}
                    />
                    <span className="filter-option-label">Employee</span>
                    <span className="filter-option-count">
                      {allUsers.filter(u => u.role === 'EMPLOYEE').length}
                    </span>
                  </label>
                </div>
              </div>

              {/* Status Filters */}
              <div className="filter-section">
                <div className="filter-section-title">Status</div>
                <div className="filter-options">
                  <label className={`filter-option ${filterActive === 'active' ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={filterActive === 'active'}
                      onChange={() => setFilterActive(filterActive === 'active' ? 'all' : 'active')}
                    />
                    <span className="filter-option-label">Active</span>
                    <span className="filter-option-count">
                      {allUsers.filter(u => u.isActive).length}
                    </span>
                  </label>
                  <label className={`filter-option ${filterActive === 'inactive' ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={filterActive === 'inactive'}
                      onChange={() => setFilterActive(filterActive === 'inactive' ? 'all' : 'inactive')}
                    />
                    <span className="filter-option-label">Inactive</span>
                    <span className="filter-option-count">
                      {allUsers.filter(u => !u.isActive).length}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="filter-panel-footer">
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                Clear All
              </button>
              <button className="apply-filters-btn" onClick={() => setShowFilterPanel(false)}>
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}

      {dealerships.length > 0 && (
        <div className="user-management-content">
          <div className="user-management-controls">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by name, email, username, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              className="add-user-btn"
              onClick={() => setShowAddModal(true)}
            >
              + Add User
            </button>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('dealershipName')} className="sortable">
                    Dealerships {sortField === 'dealershipName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('employeeRole')} className="sortable">
                    Roles {sortField === 'employeeRole' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('hireDate')} className="sortable">
                    Hire Date {sortField === 'hireDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('isActive')} className="sortable">
                    Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('lastLogin')} className="sortable">
                    Last Login {sortField === 'lastLogin' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-users">
                      {searchTerm ? 'No users match your search' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map(user => {
                    const isExpanded = expandedUsers.has(user.employmentId);
                    return (
                      <tr key={user.employmentId} className={`${!user.isActive ? 'inactive-row' : ''} ${isExpanded ? 'expanded' : ''}`}>
                        <td data-label="Name" className="mobile-card-main">
                          <span className="desktop-name">{user.name}</span>
                          <div className="mobile-card-header">
                            <div className="mobile-card-info">
                              <div className="mobile-card-name">{user.name}</div>
                              <div className="mobile-card-email">{user.email}</div>
                            </div>
                            <div className="mobile-card-actions">
                              {isOwner && (
                                <button
                                  className="btn-edit-compact"
                                  onClick={() => {
                                    // Ensure roles is always an array
                                    const userWithRoles = {
                                      ...user,
                                      roles: Array.isArray(user.roles) ? user.roles : (typeof user.roles === 'string' ? JSON.parse(user.roles) : [])
                                    };
                                    setEditingUser(userWithRoles);
                                    setShowEditModal(true);
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                className="btn-expand"
                                onClick={() => {
                                  const newExpanded = new Set(expandedUsers);
                                  if (isExpanded) {
                                    newExpanded.delete(user.employmentId);
                                  } else {
                                    newExpanded.add(user.employmentId);
                                  }
                                  setExpandedUsers(newExpanded);
                                }}
                              >
                                {isExpanded ? '▲' : '▼'}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td data-label="Dealerships" className="mobile-expandable">
                          <div className="roles-container">
                            {user.dealershipIds && user.dealershipIds.length > 0
                              ? user.dealershipIds.map(id => {
                                  const dealership = dealerships.find(d => d.id === id);
                                  return dealership ? (
                                    <span key={id} className="role-tag">{dealership.name}</span>
                                  ) : null;
                                }).filter(Boolean)
                              : <span style={{fontSize: '12px', color: '#666'}}>{user.dealershipName || '-'}</span>}
                          </div>
                        </td>
                        <td data-label="Email" className="mobile-expandable">{user.email}</td>
                        <td data-label="Roles" className="mobile-expandable">
                          <div className="roles-container">
                            {user.roles && user.roles.length > 0
                              ? user.roles.map(role => (
                                  <span key={role} className="role-tag">
                                    {role}
                                  </span>
                                ))
                              : user.employeeRole || '-'}
                          </div>
                        </td>
                        <td data-label="Hire Date" className="mobile-expandable">{new Date(user.hireDate).toLocaleDateString()}</td>
                        <td data-label="Status" className="mobile-expandable">
                          {user.invitePending ? (
                            <span className="status-badge invite-pending">Invite Sent</span>
                          ) : (
                            <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </td>
                        <td data-label="Last Login" className="mobile-expandable">
                          {user.lastLogin
                            ? new Date(user.lastLogin).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td data-label="Actions" className="mobile-expandable">
                          <div className="action-buttons">
                            {isSuperAdmin && user.isActive && user.userId && user.userId !== adminUserId && (
                              <button
                                className="btn-manage"
                                onClick={() => handleViewAs(user.userId)}
                                title="Impersonate this user (see exactly what they see)"
                              >
                                View as
                              </button>
                            )}
                            {isOwner && (
                              <button
                                className="btn-edit"
                                onClick={() => {
                                  // Convert single dealershipId to dealershipIds array if needed
                                  const userToEdit = {
                                    ...user,
                                    dealershipIds: user.dealershipIds || (user.dealershipId ? [user.dealershipId] : [])
                                  };
                                  setEditingUser(userToEdit);
                                  setShowEditModal(true);
                                }}
                              >
                                Edit
                              </button>
                            )}
                            {user.isActive ? (
                              <button
                                className="btn-deactivate"
                                onClick={() => handleDeactivateUser(user)}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn-reactivate"
                                  onClick={() => handleReactivateUser(user)}
                                >
                                  Reactivate
                                </button>
                                {isOwner && (
                                  <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteUser(user)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add New User</h2>
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label>Dealerships *</label>
                
                {/* Selected Dealerships Display */}
                {newUser.dealershipIds && newUser.dealershipIds.length > 0 && (
                  <div className="selected-roles">
                    {newUser.dealershipIds.map(dealershipId => {
                      const dealership = dealerships.find(d => d.id === dealershipId);
                      return dealership ? (
                        <span key={dealershipId} className="role-badge">
                          {dealership.name}
                          <button
                            type="button"
                            className="role-badge-remove"
                            onClick={() => removeDealershipFromNew(dealershipId)}
                            title="Remove dealership"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Searchable Dropdown */}
                <div className="roles-dropdown-container">
                  <input
                    type="text"
                    className="roles-search-input"
                    placeholder="Search dealerships..."
                    value={dealershipsSearchInput}
                    onChange={(e) => setDealershipsSearchInput(e.target.value)}
                    onFocus={() => setDealershipsDropdownOpen(true)}
                  />
                  
                  {dealershipsDropdownOpen && (
                    <>
                      <div 
                        className="dropdown-overlay"
                        onClick={() => setDealershipsDropdownOpen(false)}
                      />
                      <div className="roles-dropdown-menu">
                        {getFilteredDealerships(dealershipsSearchInput).map(dealership => (
                          <button
                            key={dealership.id}
                            type="button"
                            className={`dropdown-item ${newUser.dealershipIds.includes(dealership.id) ? 'selected' : ''}`}
                            onClick={() => handleAddDealershipFromDropdown(dealership.id)}
                          >
                            <span className="dropdown-item-checkmark">
                              {newUser.dealershipIds.includes(dealership.id) ? '✓' : ''}
                            </span>
                            {dealership.name} - {dealership.location}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Confirm Email *</label>
                <input
                  type="email"
                  required
                  value={newUser.emailConfirm}
                  onChange={(e) => setNewUser({ ...newUser, emailConfirm: e.target.value })}
                  onPaste={(e) => e.preventDefault()}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Roles</label>
                
                {/* Selected Roles Display */}
                {newUser.roles && newUser.roles.length > 0 && (
                  <div className="selected-roles">
                    {newUser.roles.map(role => (
                      <span key={role} className="role-badge">
                        {role}
                        <button
                          type="button"
                          className="role-badge-remove"
                          onClick={() => removeRoleFromNew(role)}
                          title="Remove role"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Searchable Dropdown */}
                <div className="roles-dropdown-container">
                  <input
                    type="text"
                    className="roles-search-input"
                    placeholder="Search roles..."
                    value={rolesSearchInput}
                    onChange={(e) => setRolesSearchInput(e.target.value)}
                    onFocus={() => setRolesDropdownOpen(true)}
                  />
                  
                  {rolesDropdownOpen && (
                    <>
                      <div 
                        className="dropdown-overlay"
                        onClick={() => setRolesDropdownOpen(false)}
                      />
                      <div className="roles-dropdown-menu">
                        {getFilteredRoles(rolesSearchInput).map(role => (
                          <button
                            key={role}
                            type="button"
                            className={`dropdown-item ${newUser.roles.includes(role) ? 'selected' : ''}`}
                            onClick={() => handleAddRoleFromDropdown(role)}
                          >
                            <span className="dropdown-item-checkmark">
                              {newUser.roles.includes(role) ? '✓' : ''}
                            </span>
                            {role}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {isPlatformStaff && (
                <div className="form-group" style={{ marginTop: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newUser.activateImmediately}
                      onChange={(e) => setNewUser({ ...newUser, activateImmediately: e.target.checked })}
                      style={{ width: 'auto' }}
                    />
                    <span>Activate immediately (skip email invite, set a temporary password)</span>
                  </label>
                </div>
              )}

              {isPlatformStaff && newUser.activateImmediately && (
                <div className="form-group">
                  <label>Temporary Password</label>
                  <input
                    type="text"
                    value={newUser.tempPassword}
                    onChange={(e) => setNewUser({ ...newUser, tempPassword: e.target.value })}
                    placeholder="Leave blank to auto-generate (min 8 characters)"
                  />
                </div>
              )}

              {addModalError && (
                <div className="login-error" style={{ marginBottom: 12 }}>
                  {addModalError}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => { setShowAddModal(false); setAddModalError(null); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {newUser.activateImmediately ? 'Create Active User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit User</h2>

            {editingUser.invitePending && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, padding: '10px 14px', background: 'rgba(255,180,60,0.08)', border: '1px solid rgba(255,180,60,0.25)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="status-badge invite-pending">Invite Sent</span>
                  <span style={{ fontSize: 13, color: 'rgba(220,200,130,0.7)' }}>Account not yet activated</span>
                </div>
                <button
                  type="button"
                  className="btn-edit"
                  style={{ margin: 0 }}
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/api/user-management/users/${editingUser.userId}/resend-invite`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                      });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.error || 'Failed to resend invite');
                      setAlertModal({ isOpen: true, title: 'Invite Sent', message: `A new invite email has been sent to ${editingUser.email}.` });
                    } catch (err) {
                      setAlertModal({ isOpen: true, title: 'Error', message: err.message });
                    }
                  }}
                >
                  Resend Invite
                </button>
              </div>
            )}

            <form onSubmit={handleEditUser}>
              <div className="form-group">
                <label>Dealerships *</label>
                
                {/* Selected Dealerships Display */}
                {editingUser.dealershipIds && editingUser.dealershipIds.length > 0 && (
                  <div className="selected-roles">
                    {editingUser.dealershipIds.map(dealershipId => {
                      const dealership = dealerships.find(d => d.id === dealershipId);
                      return dealership ? (
                        <span key={dealershipId} className="role-badge">
                          {dealership.name}
                          <button
                            type="button"
                            className="role-badge-remove"
                            onClick={() => removeDealershipFromEditing(dealershipId)}
                            title="Remove dealership"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Searchable Dropdown */}
                <div className="roles-dropdown-container">
                  <input
                    type="text"
                    className="roles-search-input"
                    placeholder="Search dealerships..."
                    value={editingDealershipsSearchInput}
                    onChange={(e) => setEditingDealershipsSearchInput(e.target.value)}
                    onFocus={() => setEditingDealershipsDropdownOpen(true)}
                  />
                  
                  {editingDealershipsDropdownOpen && (
                    <>
                      <div 
                        className="dropdown-overlay"
                        onClick={() => setEditingDealershipsDropdownOpen(false)}
                      />
                      <div className="roles-dropdown-menu">
                        {getFilteredDealerships(editingDealershipsSearchInput).map(dealership => (
                          <button
                            key={dealership.id}
                            type="button"
                            className={`dropdown-item ${editingUser.dealershipIds?.includes(dealership.id) ? 'selected' : ''}`}
                            onClick={() => handleAddEditingDealershipFromDropdown(dealership.id)}
                          >
                            <span className="dropdown-item-checkmark">
                              {editingUser.dealershipIds?.includes(dealership.id) ? '✓' : ''}
                            </span>
                            {dealership.name} - {dealership.location}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Roles</label>
                
                {/* Selected Roles Display */}
                {editingUser.roles && editingUser.roles.length > 0 && (
                  <div className="selected-roles">
                    {editingUser.roles.map(role => (
                      <span key={role} className="role-badge">
                        {role}
                        <button
                          type="button"
                          className="role-badge-remove"
                          onClick={() => removeRoleFromEditing(role)}
                          title="Remove role"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Searchable Dropdown */}
                <div className="roles-dropdown-container">
                  <input
                    type="text"
                    className="roles-search-input"
                    placeholder="Search roles..."
                    value={editingRolesSearchInput}
                    onChange={(e) => setEditingRolesSearchInput(e.target.value)}
                    onFocus={() => setEditingRolesDropdownOpen(true)}
                  />
                  
                  {editingRolesDropdownOpen && (
                    <>
                      <div 
                        className="dropdown-overlay"
                        onClick={() => setEditingRolesDropdownOpen(false)}
                      />
                      <div className="roles-dropdown-menu">
                        {getFilteredRoles(editingRolesSearchInput).map(role => (
                          <button
                            key={role}
                            type="button"
                            className={`dropdown-item ${editingUser?.roles?.includes(role) ? 'selected' : ''}`}
                            onClick={() => handleEditRoleFromDropdown(role)}
                          >
                            <span className="dropdown-item-checkmark">
                              {editingUser?.roles?.includes(role) ? '✓' : ''}
                            </span>
                            {role}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Roles Modal */}
      {showManageRolesModal && (
        <ManageRoles user={user} managedOwner={managedOwner} dealershipBetaFeatures={dealershipBetaFeatures} onClose={() => setShowManageRolesModal(false)} />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmStyle={confirmModal.confirmStyle}
        hideCancel={confirmModal.hideCancel}
      />

      {/* Alert Modal (for errors and success messages) */}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
    </div>
  );
}
